import React, { useState, useEffect } from 'react';
import logos from '../common/Logos';
import { truncateText } from '../../utils/helpers';
import { fetchVirtualAccounts, toggleVirtualAccountSync } from '../../services/api';
import theme from '../../theme';
import NotificationBell from '../Notifications/NotificationBell';

function AccountList({ accounts, onSelect, onNotificationSelect }) {
  const [virtualAccounts, setVirtualAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState({});

  useEffect(() => {
    const loadVirtualAccounts = async () => {
      setIsLoading(true);
      try {
        const accounts = await fetchVirtualAccounts();
        console.log('Fetched virtual accounts:', accounts);

        setVirtualAccounts((accounts || []).filter(va => va.sync_enabled));
      } catch (error) {
        console.error('Error loading virtual accounts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadVirtualAccounts();
  }, []);

  const handleToggleSync = async (e, virtualAccountId, currentSyncState) => {
    e.stopPropagation();
    setSyncLoading(prev => ({ ...prev, [virtualAccountId]: true }));

    try {
      const result = await toggleVirtualAccountSync(virtualAccountId, !currentSyncState);
      if (result) {
        setVirtualAccounts(prev =>
          prev.map(va =>
            va.id === virtualAccountId
              ? { ...va, sync_enabled: !currentSyncState }
              : va
          ).filter(va => va.sync_enabled)
        );
      }
    } catch (error) {
      console.error('Error toggling sync:', error);
    } finally {
      setSyncLoading(prev => ({ ...prev, [virtualAccountId]: false }));
    }
  };

  const notificationContextForAccount = (account) => (
    account.broker === 'okx'
      ? { brokers: [account.broker] }
      : {
        brokers: [account.broker],
        accounts: account.account ? [account.account] : [],
      }
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {accounts.map((account) => (
        <div key={`account-${account.id}`} className="relative">
          <button
            onClick={() => onSelect(account)}
            className={`group flex w-full items-center gap-4 p-4 pr-12 ${theme.cardBg} rounded-2xl ${theme.cardBorder} cursor-pointer transition-all duration-200 hover:shadow-md hover:border-olive-300 hover:-translate-y-0.5 active:scale-[0.98] text-left`}
          >
            <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 bg-olive-50 flex items-center justify-center border border-olive-100">
              <img
                src={logos[account.broker]}
                alt={`${account.broker} logo`}
                className="w-8 h-8 object-contain"
              />
            </div>
            <div className="min-w-0">
              <h3 className={`text-sm font-bold ${theme.textPrimary} tracking-wide group-hover:text-olive-600 transition-colors`}>
                {account.broker.toUpperCase()}
              </h3>
              <p className={`text-xs ${theme.textMuted} truncate`}>{truncateText(account.account, 12)}</p>
            </div>
            <svg className="w-4 h-4 ml-auto text-olive-300 group-hover:text-olive-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div className="absolute top-2 right-2 z-10">
            <NotificationBell
              size="sm"
              align="right"
              context={notificationContextForAccount(account)}
              title={`${account.broker.toUpperCase()} · ${account.account}`}
              onItemClick={(n) => (onNotificationSelect ? onNotificationSelect(account, n) : onSelect(account))}
            />
          </div>
        </div>
      ))}

      {virtualAccounts.map((virtualAccount) => (
        <div
          key={`virtual-${virtualAccount.id}`}
          onClick={() => onSelect({
            id: `virtual-${virtualAccount.id}`,
            broker: 'virtual',
            account: virtualAccount.name,
            virtualAccountId: virtualAccount.id,
            sourceAccount: virtualAccount.linked_accounts.find(acc => acc.is_source),
            linkedAccounts: virtualAccount.linked_accounts
          })}
          className={`group flex items-center gap-4 p-4 ${theme.cardBg} rounded-2xl border-2 border-olive-200 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-olive-400 hover:-translate-y-0.5 active:scale-[0.98]`}
        >
          <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 bg-olive-50 flex items-center justify-center border border-olive-200">
            <img
              src={logos['virtual']}
              alt="Virtual account logo"
              className="w-8 h-8 object-contain"
            />
          </div>
          <div className="flex-grow min-w-0">
            <div className="flex justify-between items-center">
              <h3 className={`text-sm font-bold ${theme.textPrimary} group-hover:text-olive-600 transition-colors truncate`}>
                {virtualAccount.name}
              </h3>
              <button
                onClick={(e) => handleToggleSync(e, virtualAccount.id, virtualAccount.sync_enabled)}
                disabled={syncLoading[virtualAccount.id]}
                className={`relative inline-flex items-center h-5 rounded-full w-9 flex-shrink-0 ml-2 transition-colors duration-200 ${virtualAccount.sync_enabled ? 'bg-emerald-500' : 'bg-olive-300'
                  }`}
              >
                <span
                  className={`${virtualAccount.sync_enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'
                    } inline-block w-3.5 h-3.5 transform bg-white rounded-full transition-transform shadow-sm`}
                />
              </button>
            </div>
            <p className={`text-xs ${theme.textMuted} mt-0.5`}>
              {virtualAccount.linked_accounts.length} linked
            </p>
            <p className="text-[10px] text-olive-500 mt-0.5 font-medium">
              Source: {virtualAccount.linked_accounts.find(acc => acc.is_source)?.broker.toUpperCase() || 'Unknown'}
            </p>
          </div>
        </div>
      ))}

      {isLoading && (
        <div className="col-span-full flex justify-center items-center py-8">
          <div className={`w-6 h-6 border-2 ${theme.spinner} rounded-full animate-spin`} />
        </div>
      )}
    </div>
  );
}

export default AccountList;
